import { useMemo, useRef, useState } from 'react';
import {
  useUploadCsvMutation, useConfirmCsvMappingMutation, useIntakeBatchQuery,
  type CsvUploadResult,
} from './api.js';
import { Alert, Button, Card, Spinner } from './ui.js';

/**
 * Importing a CSV: look, map, check, commit.
 *
 * Nothing is created on upload. The file is parsed, the columns are guessed, and a
 * bounded sample comes back — because a mis-detected header committing four thousand rows
 * under the wrong field is not a mistake anybody wants to undo by hand.
 *
 * The file stays in the browser between the two steps and is sent again on confirm. That
 * is a deliberate trade: re-parsing costs milliseconds, and it is what lets the product
 * say "we do not keep your contact list" without a qualification.
 */

/*
 * The statuses that mean the worker is finished with this batch. `extracting` and the
 * earlier states mean rows are still being resolved, which is exactly the window the
 * import screen used to report as a completed result.
 */
const TERMINAL_BATCH = new Set([
  'review_required', 'ready', 'completed', 'partially_completed', 'failed', 'cancelled',
]);

type Step = 'choose' | 'map' | 'done';

/**
 * The columns that can say who a row is about. A typed phone column (Mobile, Office,
 * Direct, Home) identifies a person as well as a plain Phone one does; a fax does not —
 * nobody is found by their fax number.
 */
export const IDENTIFYING_FIELDS: readonly string[] = [
  'email', 'phone', 'mobilePhone', 'workPhone', 'directPhone', 'homePhone',
  'fullName', 'firstName', 'lastName',
];

const CONFIDENCE_NOTE: Record<string, string> = {
  exact: '',
  alias: 'matched by name',
  ambiguous: 'could mean more than one thing — please choose',
  none: 'not recognised',
};

export function CsvImport({ onClose, onViewInReview }: {
  onClose: () => void;
  /** Where an accepted import is decided: Review, filtered to imports. */
  onViewInReview?: () => void;
}) {
  const [upload, { isLoading: uploading }] = useUploadCsvMutation();
  const [confirm, { isLoading: confirming }] = useConfirmCsvMappingMutation();
  const pickerRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CsvUploadResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Record<string, number> | null>(null);
  const [doneBatchId, setDoneBatchId] = useState<string | null>(null);

  /*
   * The same polling the batch screen uses, rather than a second mechanism.
   *
   * Stops once the batch reaches a terminal status, so a finished import is not asking
   * the server about itself every two seconds for as long as the tab is open.
   */
  const { data: live } = useIntakeBatchQuery(doneBatchId ?? '', {
    skip: doneBatchId === null,
    pollingInterval: 2_000,
  });

  const choose = async (input: HTMLInputElement) => {
    const f = input.files?.[0] ?? null;
    input.value = '';
    if (!f) return;
    setError(null);
    setFile(f);
    try {
      const res = await upload(f).unwrap();
      if ((res as { alreadyExists?: boolean }).alreadyExists) {
        setError('This exact file has already been imported. Find it in Review, or choose a different file.');
        setFile(null);
        return;
      }
      setResult(res);
      // Start from the suggestions, with ambiguous columns left for the person.
      setMapping(Object.fromEntries(
        res.suggestions.filter((s) => s.field !== null).map((s) => [String(s.columnIndex), s.field!]),
      ));
      setStep('map');
    } catch (e) {
      const data = (e as { data?: { message?: string; error?: string } }).data;
      setError(data?.message ?? 'That file could not be read as a CSV.');
      setFile(null);
    }
  };

  /** Two columns cannot fill one field; the screen says so before the server has to. */
  const collisions = useMemo(() => {
    const seen = new Map<string, number>();
    for (const f of Object.values(mapping)) seen.set(f, (seen.get(f) ?? 0) + 1);
    return new Set([...seen].filter(([, n]) => n > 1).map(([f]) => f));
  }, [mapping]);

  const identifying = useMemo(
    () => Object.values(mapping).some((f) => IDENTIFYING_FIELDS.includes(f)),
    [mapping],
  );

  const commit = async () => {
    if (!file || !result) return;
    setError(null);
    try {
      /*
       * The response is 202 Accepted, and its tally is a snapshot of work that has not
       * happened yet — every row is still `pending` at that instant.
       *
       * It used to be rendered as the finished result, which is why an import of one
       * contact reported four zeros while the Review badge already showed 1. The tally is
       * kept only so the screen has something to show for the first moment; from then on
       * the batch itself is the authority, polled below.
       */
      const res = await confirm({ file, batchId: result.batch.id, mapping }).unwrap();
      setSummary(res.tally.byStatus);
      setDoneBatchId(res.batch.id);
      setStep('done');
    } catch (e) {
      const data = (e as { data?: { message?: string; error?: string } }).data;
      setError(data?.message ?? 'The import could not be completed.');
    }
  };

  /* ── Choose ─────────────────────────────────────────────────────────── */
  if (step === 'choose') {
    return (
      <Card className="p-5">
        <h3 className="text-base font-semibold text-slate-900">Import contacts from a file</h3>
        <p className="mt-1 text-sm text-slate-600">
          A CSV exported from your CRM, spreadsheet or email tool. Nothing is added until
          you have seen how the columns were understood.
        </p>
        <input
          ref={pickerRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={(e) => void choose(e.currentTarget)}
        />
        <div className="mt-4 flex flex-wrap gap-2">
          <Button className="min-h-11" onClick={() => pickerRef.current?.click()} disabled={uploading}>
            {uploading ? 'Reading…' : 'Choose a CSV file'}
          </Button>
          <Button className="min-h-11" variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
        {error ? <div className="mt-4"><Alert tone="error">{error}</Alert></div> : null}
        {uploading ? <div className="mt-4"><Spinner label="Reading the file…" /></div> : null}
      </Card>
    );
  }

  /* ── Map and preview ────────────────────────────────────────────────── */
  if (step === 'map' && result) {
    const mappedCount = Object.keys(mapping).length;
    const overLimit = result.overLimit ?? null;
    const importable = result.importableRows ?? result.totalRows;
    const blocked = collisions.size > 0 || !identifying || overLimit !== null;

    return (
      <Card className="p-5">
        <h3 className="text-base font-semibold text-slate-900">Check the columns</h3>
        <p className="mt-1 text-sm text-slate-600">
          {result.truncated
            ? `More than ${importable.toLocaleString('en-US')} rows`
            : `${result.totalRows} row${result.totalRows === 1 ? '' : 's'}`} found ·{' '}
          {result.headers.length} columns · {mappedCount} mapped
          {result.truncated && !overLimit ? ' · only the first rows will be imported' : ''}
        </p>

        {overLimit ? (
          <div className="mt-3" data-csv-over-limit>
            <Alert tone="error">
              This file has {overLimit.totalRows === null
                ? `more than ${importable.toLocaleString('en-US')}`
                : overLimit.totalRows.toLocaleString('en-US')} rows. One import allows up
              to {overLimit.limit.toLocaleString('en-US')}, so nothing will be imported from it. Split
              it into smaller files and import each one.
            </Alert>
          </div>
        ) : result.truncated ? (
          <div className="mt-3">
            <Alert tone="info">
              This file is larger than one import allows. The first rows will be imported;
              split the file to bring in the rest.
            </Alert>
          </div>
        ) : null}

        {/* Stacks on a phone, two columns on a wide screen. No table, so nothing to
            scroll sideways. */}
        <ul className="mt-4 space-y-2">
          {result.suggestions.map((s) => {
            const value = mapping[String(s.columnIndex)] ?? '';
            const clash = value !== '' && collisions.has(value);
            return (
              <li
                key={s.columnIndex}
                className="flex flex-col gap-2 rounded-md border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{s.header}</p>
                  <p className="text-xs text-slate-500">
                    {CONFIDENCE_NOTE[s.confidence] || 'recognised'}
                  </p>
                </div>
                <label className="shrink-0">
                  <span className="sr-only">Map column {s.header}</span>
                  <select
                    value={value}
                    onChange={(e) => setMapping((m) => {
                      const next = { ...m };
                      if (e.target.value === '') delete next[String(s.columnIndex)];
                      else next[String(s.columnIndex)] = e.target.value;
                      return next;
                    })}
                    className={`min-h-11 w-full rounded-md border bg-white px-2 text-sm sm:w-56 ${
                      clash ? 'border-red-400' : 'border-slate-300'
                    }`}
                  >
                    <option value="">Ignore this column</option>
                    {result.fields.map((f) => (
                      <option key={f.field} value={f.field}>{f.label}</option>
                    ))}
                  </select>
                </label>
              </li>
            );
          })}
        </ul>

        {collisions.size > 0 ? (
          <div className="mt-4">
            <Alert tone="error">
              Two columns are mapped to the same field. Each field holds one value, so
              choose which column should fill it.
            </Alert>
          </div>
        ) : null}
        {!identifying ? (
          <div className="mt-4">
            <Alert tone="error">
              Map at least an email, a phone number or a name — without one there is no way
              to tell who each row is about.
            </Alert>
          </div>
        ) : null}

        {/* A sample, not the file. Scrolls inside its own box. */}
        <div className="mt-5">
          <p className="text-sm font-medium text-slate-700">
            First {result.sample.length} row{result.sample.length === 1 ? '' : 's'}
          </p>
          <div className="mt-2 overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-1.5 font-semibold text-slate-500">#</th>
                  {result.headers.map((h) => (
                    <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.sample.map((r) => (
                  <tr key={r.rowNumber} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-slate-400">{r.rowNumber}</td>
                    {result.headers.map((h, i) => (
                      <td key={h} className="max-w-[16rem] truncate px-2 py-1.5 text-slate-700">
                        {r.values[i] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {error ? <div className="mt-4"><Alert tone="error">{error}</Alert></div> : null}

        <div className="mt-5 flex flex-wrap gap-2">
          <Button className="min-h-11" onClick={commit} disabled={blocked || confirming}>
            {confirming ? 'Importing…' : `Import ${importable.toLocaleString('en-US')} row${importable === 1 ? '' : 's'}`}
          </Button>
          <Button className="min-h-11" variant="secondary" onClick={onClose} disabled={confirming}>
            Cancel
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Importing adds these to your review queue. Nothing is written to your CRM.
        </p>
      </Card>
    );
  }

  /* ── Done ───────────────────────────────────────────────────────────── */
  /*
   * The server accepted the rows; a worker resolves them. Until it has, this screen says
   * so rather than reporting counts that are merely the starting state.
   */
  const settled = live?.batch.status !== undefined && TERMINAL_BATCH.has(live.batch.status);
  const n = (k: string) => (settled ? live!.tally.byStatus[k] ?? 0 : summary?.[k] ?? 0);
  return (
    <Card className="p-5">
      <h3 className="text-base font-semibold text-slate-900">
        {settled ? 'Import complete' : 'Importing…'}
      </h3>
      <p className="mt-1 text-sm text-slate-600">
        {settled
          ? 'Nothing has been written to your CRM. These are ready for you to review.'
          : 'Your file has been accepted. Each row is being checked against your CRM; '
            + 'this page updates as they finish.'}
      </p>
      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Needs review', n('needs_review')],
          ['Duplicates', n('duplicate')],
          ['Invalid', n('invalid')],
          ['Ready', n('ready')],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-md border border-slate-200 p-3">
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className="mt-0.5 text-xl font-semibold text-slate-900">
              {settled ? value : '—'}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        {onViewInReview ? (
          <Button className="min-h-11" onClick={onViewInReview}>View in Review</Button>
        ) : null}
        <Button className="min-h-11" variant={onViewInReview ? 'secondary' : 'primary'} onClick={onClose}>
          Back to Capture
        </Button>
      </div>
    </Card>
  );
}
